
import { Dispatch, useCallback, useState } from "react";
import { Tooltip } from "primereact/tooltip";

import fexdexLogo from "../../../assets/images/fedex-logo.png";
import uspsLogo from "../../../assets/images/usps-logo.png";
import checkPointIcon from '../../../assets/icons/mailing/check_point_icon.svg';

import { classNames } from "primereact/utils";
import { transportTypes } from "../../../config/config";
import { Button } from "primereact/button";

const deliverySteps = [
    {
      heading: 'Send your documents to the IRS',
      description: `We will send your signed cover letter, two copies of your signed 83(b) election form, and a postage-paid return envelope to the IRS.`,
    },
    {
      heading: 'Provide a tracking number and proof of delivery',
      description: `Once your documents are shipped, we will provide you with a tracking number and proof of delivery, if and once available. If you select "USPS Certified Mail", we will also provide you with the postmarked Certified Mail Receipt.`,
    },
    {
      heading: 'Provide IRS date-stamped copy',
      description: 'If and once received, we will provide you with the IRS date-stamped copy of your filing.',
    },
    {
      heading: 'Store your documents for 90 days',
      description: `We will store any originals or digital copies of your documents for 90 days after placing your order. You may request that we mail you the originals within this period.`,
    },
  ];

type PropTypes = {
    fedexPrice: number;
    uspsPrice: number;
    deliveryService: string;
    handlePreviousStep: () => void;
    handleNextStep: () => void;
    setSelectedDeliveryService:Dispatch<React.SetStateAction<string>>;
}
function DeliveryService(props: PropTypes) {
    const { 
        setSelectedDeliveryService,
        handlePreviousStep,
        handleNextStep,
        deliveryService,
        fedexPrice,
        uspsPrice
     } = props;

 
     const [isLoading, setLoading] = useState(false);
     const onNextStepHandler = useCallback(async() => {
        setLoading(true)
        handleNextStep()
     },[handleNextStep]);

    return (
        <div className="w-full h-full flex justify-content-center relative">
            <div className="p-5 w-8 flex bg-white flex- flex-column gap-2 relative" >
                <span className="text-2xl font-medium pb-3">Please select your preferred shipping method:</span>
                <div className="flex w-full justify-content-between gap-2">
                    <div className={classNames("w-6 p-3 cursor-pointer border-1 border-200 border-solid flex flex-column gap-2", {
                        'border-blue-500': deliveryService === transportTypes.FEDEX,
                        'hover:border-blue-500': deliveryService !== transportTypes.FEDEX
                    })} onClick={() => setSelectedDeliveryService(transportTypes.FEDEX)}>
                        <div className="flex align-items-center gap-2">
                            <div className="w-2 pl-2">
                                <img src={fexdexLogo} alt="FedEx" className="w-full h-auto" />
                            </div>
                            <div className="text-3xl font-medium">FedEx 2Day</div>
                        </div>
                        <div className="pl-2 font-medium text-lg">
                            <div className="py-2 font-italic">Recommended</div>
                            FedEx 2Day is an IRS-designated private delivery service that is legally
                            equivalent to USPS Certified Mail for purposes of proving timely mailing and delivery. 
                            It is typically faster and more reliable.
                        </div>
                        <div className="pl-2 mt-auto font-bold flex align-items-baseline gap-2">
                            <span className="text-4xl">{`$${fedexPrice}`}</span>
                            <span className="text-lg font-medium">One-time payment</span>
                        </div>
                    </div>

                    <div className={classNames("w-6 p-3 cursor-pointer border-1 border-200 border-solid flex flex-column gap-2", {
                        'border-blue-500': deliveryService == transportTypes.USPS,
                        'hover:border-blue-500': deliveryService !== transportTypes.USPS
                    })} onClick={() => setSelectedDeliveryService(transportTypes.USPS)}>
                        <div className="flex align-items-baseline gap-2">
                            <div className="w-2 pl-2">
                                <img src={uspsLogo} alt="USPS" className="w-full h-auto" />
                            </div>
                            <div className="text-3xl font-medium">USPS Certified Mail</div>
                        </div>
                        <div className="pl-2 font-medium text-lg">
                            <div className="py-2 font-italic">Conventional</div>
                                USPS Certified Mail is the conventional choice for mailing an 83(b)
                                election to the IRS. This product is priced higher due to additional 
                                handling requirements and occasional reliability issues.
                            </div>
                        <div className="pl-2 mt-auto font-bold flex align-items-baseline gap-2">
                            <span className="text-4xl">{`$${uspsPrice}`}</span>
                            <span className="text-lg font-medium">One-time payment</span>
                        </div>
                    </div>
                </div>
                <div className="flex flex-column gap-2 mt-6">
                    <div className="mb-4">
                        <i className="pi pi-info-circle mr-2" />
                        <span className="font-medium text-2xl">After you sign the cover letter and 83(b) election form, we will:</span>
                    </div>
                    <ul className="list-none flex flex-column gap-2 pl-0">
                        {
                            deliverySteps.map((step, index) => (
                                <li key={index} className="flex align-items-start gap-3 mb-2">
                                     <img src={checkPointIcon} alt={step.heading} className="mt-2"/>
                                    <div className="flex flex-column gap-2">
                                        <span className="font-semibold text-xl">{step.heading}</span>
                                        <span className="font-medium text-lg">{step.description}</span>
                                    </div>
                                </li>
                            ))
                        }
                    </ul>
                </div>
                <div className="w-full mt-auto bottom-0 flex justify-content-end gap-2 bg-white">
                    <Button outlined 
                            label="Back To Terms" 
                            severity="secondary" 
                            onClick={handlePreviousStep}/>
                    <Button label="Pay"
                            loading={isLoading}
                            onClick={onNextStepHandler}/>
                </div>
            </div>
        </div>
    );
}

export default DeliveryService;